import './onboardingArrows.scss';

import React                       from 'react';
import { createPortal }            from 'react-dom';
import { observer }                from 'mobx-react-lite';
import onboardingStore             from '@/TokamapEditor/stores/onboardingStore';
import currentEditorStore          from '@/TokamapEditor/stores/currentEditorStore';
import { ONBOARDING_TARGET_CLASS } from '@/TokamapEditor/stores/onboardingStore';
import useDesignTokens             from '@/hooks/useDesignTokens';

const ARROW_SIZE = 56;
const ARROW_GAP = 0;
const ARROW_PATH = 'M28 6 L50 28 H38 V50 H18 V28 H6 Z';
const TOP_ZONE_RATIO = 0.4;

type Pointer = { key: string; left: number; top: number; direction: 'up' | 'left' | 'right' };

const modalOpen = () => [...document.querySelectorAll('.ant-modal-wrap')].some(wrap => 'none' !== getComputedStyle(wrap).display);

const previewOpen = () => !!document.querySelector('.ant-popover:not(.ant-popover-hidden):has(video)');

const pointerFor = (element: Element, index: number): Pointer => {
	const rect = element.getBoundingClientRect();

	if (element.closest('.ant-popover')) {
		const left = Math.round(rect.left - ARROW_SIZE - ARROW_GAP);
		const top = Math.round(rect.top + rect.height / 2 - ARROW_SIZE / 2);

		return { key: `${index}-${left}-${top}`, left, top, direction: 'right' };
	}

	const drawer = element.closest('.ant-drawer-content')?.getBoundingClientRect();
	const spansDrawer = !!drawer && drawer.right - rect.right <= ARROW_SIZE + ARROW_GAP * 2;
	const inDrawer = !!drawer;

	if (rect.bottom < window.innerHeight * TOP_ZONE_RATIO || (inDrawer && !spansDrawer)) {
		const left = Math.round(rect.left + rect.width / 2 - ARROW_SIZE / 2);
		const top = Math.round(rect.bottom + ARROW_GAP);

		return { key: `${index}-${left}-${top}`, left, top, direction: 'up' };
	}

	const left = Math.round(rect.right + ARROW_GAP);
	const top = Math.round(rect.top + rect.height / 2 - ARROW_SIZE / 2);

	return { key: `${index}-${left}-${top}`, left, top, direction: 'left' };
};

const OnboardingArrows = observer(() => {
	const tokens = useDesignTokens();
	const target = currentEditorStore._isDrawing ? null : onboardingStore.target;
	const [pointers, setPointers] = React.useState<Pointer[]>([]);

	React.useEffect(() => {
		if (!target) {
			setPointers([]);

			return;
		}

		let frame = 0;

		const track = () => {
			const next = modalOpen() || previewOpen()
				? []
				: [...document.querySelectorAll(`.${ONBOARDING_TARGET_CLASS}`)]
					.filter(element => element.getBoundingClientRect().width)
					.map(pointerFor);

			setPointers(previous => previous.map(pointer => pointer.key).join() === next.map(pointer => pointer.key).join() ? previous : next);
			frame = requestAnimationFrame(track);
		};

		track();

		return () => cancelAnimationFrame(frame);
	}, [target]);

	return createPortal(
		pointers.map(pointer => (
			<div
				key={pointer.key}
				className={`onboarding-arrow onboarding-arrow--${pointer.direction}`}
				style={{ left: pointer.left, top: pointer.top, width: ARROW_SIZE, height: ARROW_SIZE }}
			>
				<svg width={ARROW_SIZE} height={ARROW_SIZE} viewBox="0 0 56 56" strokeLinejoin="round">
					<defs>
						<linearGradient id={`onboarding-arrow-${pointer.key}`} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0" style={{ stopColor: `color-mix(in srgb, ${tokens.color.primary} 55%, ${tokens.surface.card})` }} />
							<stop offset="1" style={{ stopColor: tokens.color.primary }} />
						</linearGradient>
					</defs>
					<path d={ARROW_PATH} fill={tokens.surface.card} stroke={tokens.surface.card} strokeWidth="11" />
					<path d={ARROW_PATH} fill={`url(#onboarding-arrow-${pointer.key})`} stroke={`url(#onboarding-arrow-${pointer.key})`} strokeWidth="5" />
					<path d="M22 31 V45" stroke={tokens.surface.card} strokeOpacity="0.45" strokeWidth="3" strokeLinecap="round" />
				</svg>
			</div>
		)),
		document.body,
	);
});

export default OnboardingArrows;
